import { useRef } from "react";

import { useLocation } from "react-router-dom";

import { LANGUAGE_FIELDS, LANGUAGES_QUERY } from "@/graphql/accounts";
import useExperts from "@/hooks/experts/useExperts";
import { useDebounce } from "@/hooks/useDebounce";
import useListControls from "@/hooks/useListControls";
import useProductFilters from "@/tpo/shop/hooks/useProductFilters";
import { gql, useQuery } from "@apollo/client";

import ExpertListCard from "./ExpertListCard";
import ExpertsListPage from "./ExpertsListPage";
import ExpertsPractitionerLocaleFilters from "./ExpertsPractitionerLocaleFilters";
import ListTemplate from "./ListTemplate";
import buildFilterSidebar from "./buildFilterSidebar";

const FILTERS_QUERY = gql`
  query FiltersQuery {
    consultationTypes: expertConsultationTypes
    practitionerTypes {
      id
      role
    }
    specialisms: healthAreas {
      id
      name
    }
    expertCountries {
      id
      name
    }
    languages(usedOnly: true) {
      ...LanguageFields
    }
  }
  ${LANGUAGE_FIELDS}
`;

export default function Practitioners() {
  const {
    data: {
      consultationTypes = [],
      practitionerTypes = [],
      specialisms = [],
      expertCountries = [],
      languages = []
    } = {}
  } = useQuery(FILTERS_QUERY);

  const location = useLocation();

  const { toggleFilter, resetFilters, removeFilter, allFilters, validatedFilters } =
    useProductFilters({
      defaultFilters: {
        consultationType: [],
        practitionerType: [],
        specialisms: [],
        country: undefined,
        language: undefined
      },
      allFilters: {
        specialisms: [...specialisms.map(s => s.name)].sort(),
        consultationType: [...consultationTypes].sort(),
        practitionerType: [...practitionerTypes.map(pt => pt.role)].sort(),
        country: expertCountries.map(sc => sc.name),
        language: languages.map(l => l.name)
      }
    });

  const applySelectFilter = (group, value) => {
    if (!value) {
      const current = validatedFilters?.[group]?.[0];
      if (current) {
        removeFilter(group, current);
      }
      return;
    }
    toggleFilter(group, value);
  };

  const controls = useListControls({
    defaultSort: "random_hash",
    sortOptions: [
      {
        label: "A - Z",
        value: "partner_user_profile__user__first_name"
      },
      {
        label: "Z - A",
        value: "-partner_user_profile__user__first_name"
      },
      {
        label: "Fee: High - Low",
        value: "fee_level"
      },
      {
        label: "Fee: Low - High",
        value: "-fee_level"
      }
    ]
  });

  const search = new URLSearchParams(location.search).get("search") || "";
  const supportedTests = new URLSearchParams(location.search).get("supported_tests") || "";

  const debouncedSearch = useDebounce(search, 200);

  const seedRef = useRef(new Date().toString());

  const { experts, fetchMore, hasMore, endCursor, loading } = useExperts({
    search: debouncedSearch,
    orderBy: controls.sort,
    consultationTypes: validatedFilters.consultationType.join(","),
    practitionerTypes: validatedFilters.practitionerType.join(","),
    specialisms: validatedFilters.specialisms.join(","),
    seed: seedRef.current,
    supportedTests,
    countryName: validatedFilters?.country?.[0] || "",
    language: validatedFilters?.language?.[0] || ""
  });

  const allFiltersForOverlay = {
    ...allFilters
  };

  delete allFiltersForOverlay["country"];
  delete allFiltersForOverlay["language"];

  const countryAndLanguageFilters = (
    <ExpertsPractitionerLocaleFilters
      expertCountries={expertCountries}
      languages={languages}
      countryValue={validatedFilters?.country?.[0] || ""}
      languageValue={validatedFilters?.language?.[0] || ""}
      onCountryChange={event => applySelectFilter("country", event.target.value)}
      onLanguageChange={event => applySelectFilter("language", event.target.value)}
    />
  );

  const filterSidebar = buildFilterSidebar({
    allFilters: allFiltersForOverlay,
    validatedFilters,
    toggleFilter,
    resetFilters,
    contentAfterTop: countryAndLanguageFilters
  });

  return (
    <ExpertsListPage
      header="Find a practitioner"
      subheader="Search our exclusive list of verified experts to assist you with your healthcare journey"
      filterSidebar={filterSidebar}
      search={search}
      allFilters={allFiltersForOverlay}
      validatedFilters={validatedFilters}
      toggleFilter={toggleFilter}
      resetFilters={resetFilters}
      controls={controls}
      contentAfterTop={countryAndLanguageFilters}
    >
      <ListTemplate.Grid
        ItemCard={ExpertListCard}
        fetchMore={fetchMore}
        hasMore={hasMore}
        endCursor={endCursor}
        loading={loading}
        items={experts}
      />
    </ExpertsListPage>
  );
}
